import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Video, Plus, Edit2, Trash2,
  Check, X, Zap, RefreshCw, ChevronDown, ChevronUp, Loader,
  Sparkles, FlaskConical, Eye, EyeOff, FileText, GripVertical,
  ClipboardList, Send, BookOpen, ChevronLeft, ChevronRight, MessageSquare,
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor,
  useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import {
  getCourse, previewConceptTranscript, generateConceptVideo, generateExtraConceptVideo,
  generateConceptQuestions, getConceptQuestions,
  updateQuestion, deleteQuestion, createQuestion, extractConcepts,
  getMyVideos, addConceptVideo, removeConceptVideo,
  improveTranscriptSection, addExampleToTranscript,
  regenerateLinkedVideo, previewExtraTranscript,
  deleteConcept, bulkDeleteConcepts, getConceptSource, updateConcept, reorderConcepts, deleteSyllabus,
  listAssignments, createAssignment, updateAssignment, togglePublishAssignment,
  fetchCoursePdfBlob, teacherChat, getTeacherChatHistory, generateVideoFromPassage,
} from '../../services/studioApi';
import { AssignmentForm } from './AssignmentManager';
import UnifiedHeader from '../../components/UnifiedHeader';
import TranscriptViewModal from '../../components/TranscriptViewModal';
import './Studio.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// ─── Sortable concept row ───────────────────────────────────────────────────
function SortableConceptItem({
  concept, index, total, isSelected, selectMode, renamingId, renameValue,
  renameSaving, selectedIds,
  onSelect, onStartRename, onRenameChange, onSaveRename, onCancelRename,
  onDelete, onToggleSelect,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: concept.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={{ ...style, display: 'flex', alignItems: 'stretch', gap: '0.3rem' }}>
      {/* Drag handle — long press activates drag */}
      {!selectMode && renamingId !== concept.id && (
        <div
          {...attributes}
          {...listeners}
          title="Hold to drag"
          style={{
            display: 'flex', alignItems: 'center', padding: '0 0.25rem',
            cursor: 'grab', color: '#ccc', touchAction: 'none', flexShrink: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#5B35A0'}
          onMouseLeave={e => e.currentTarget.style.color = '#ccc'}
        >
          <GripVertical size={14} />
        </div>
      )}

      {selectMode && (
        <input type="checkbox" checked={selectedIds.has(concept.id)}
          onChange={() => onToggleSelect(concept.id)}
          style={{ marginTop: '0.5rem', cursor: 'pointer', flexShrink: 0 }} />
      )}

      {renamingId === concept.id ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.5rem', border: '1.5px solid #5B35A0', borderRadius: 10, background: '#faf8ff' }}>
          <input
            autoFocus
            value={renameValue}
            onChange={e => onRenameChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onSaveRename(concept); if (e.key === 'Escape') onCancelRename(); }}
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.85rem', fontWeight: 600, color: '#1a1a2e' }}
          />
          <button onClick={() => onSaveRename(concept)} disabled={renameSaving}
            style={{ padding: '0.15rem 0.4rem', border: 'none', borderRadius: 5, background: '#5B35A0', color: '#fff', cursor: 'pointer', fontSize: '0.72rem', display: 'flex', alignItems: 'center' }}>
            {renameSaving ? <Loader size={11} className="spin" /> : <Check size={11} />}
          </button>
          <button onClick={onCancelRename}
            style={{ padding: '0.15rem 0.4rem', border: 'none', borderRadius: 5, background: '#eee', color: '#555', cursor: 'pointer', fontSize: '0.72rem', display: 'flex', alignItems: 'center' }}>
            <X size={11} />
          </button>
        </div>
      ) : (
        <button
          onClick={() => { if (selectMode) { onToggleSelect(concept.id); return; } onSelect(concept); }}
          style={{
            flex: 1, background: isSelected ? '#f3eeff' : 'transparent',
            border: isSelected ? '1.5px solid #5B35A0' : '1.5px solid transparent',
            borderRadius: 10, padding: '0.6rem 0.75rem',
            textAlign: 'left', cursor: 'pointer', transition: 'all 0.15s',
          }}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e', marginBottom: '0.15rem' }}>
            {concept.title}
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', fontSize: '0.72rem', color: '#888', flexWrap: 'wrap' }}>
            {(['ready', 'completed'].includes(concept.video_status) ||
              (concept.linked_videos || []).some(lv => ['ready', 'completed'].includes(lv.status))
            ) && <span style={{ color: '#0DBFB0' }}>▶ Video</span>}
            {concept.total_questions > 0 && <span>{concept.approved_questions}/{concept.total_questions} Q</span>}
            {concept.page_reference && <span style={{ color: '#7c6bb0' }}>📖 p.{concept.page_reference}</span>}
          </div>
        </button>
      )}

      {!selectMode && renamingId !== concept.id && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
          <button title="Rename" onClick={(e) => { e.stopPropagation(); onStartRename(concept); }}
            style={{ padding: '0 0.4rem', background: 'transparent', border: 'none', cursor: 'pointer', color: '#ccc', borderRadius: 6, display: 'flex', alignItems: 'center', flex: 1 }}
            onMouseEnter={e => e.currentTarget.style.color = '#5B35A0'}
            onMouseLeave={e => e.currentTarget.style.color = '#ccc'}>
            <Edit2 size={12} />
          </button>
          <button title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(concept); }}
            style={{ padding: '0 0.4rem', background: 'transparent', border: 'none', cursor: 'pointer', color: '#ccc', borderRadius: 6, display: 'flex', alignItems: 'center', flex: 1 }}
            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
            onMouseLeave={e => e.currentTarget.style.color = '#ccc'}>
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Generate Questions config modal ───────────────────────────────────────
function GenerateQModal({ onGenerate, onClose }) {
  const { language } = useLanguage();
  const tT = translations[language]?.studio?.teacher || {};
  const gq = tT.genQ || {};

  const ALL_TYPES = [
    { id: 'multiple_choice', label: gq.typeMultiple   || 'Multiple choice' },
    { id: 'numeric',         label: gq.typeNumeric     || 'Numeric / calculation' },
    { id: 'open_ended',      label: gq.typeOpen        || 'Open-ended' },
    { id: 'true_false',      label: gq.typeTrueFalse   || 'True / False' },
  ];

  const [types, setTypes]   = useState(['multiple_choice', 'numeric']);
  const [count, setCount]   = useState(5);
  const [diff,  setDiff]    = useState('mixed');
  const [sample, setSample] = useState('');

  const toggleType = (id) =>
    setTypes(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);

  const handleSubmit = () => {
    if (types.length === 0) return;
    onGenerate({ types, count, difficulty: diff, sample_questions: sample || null });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', width: '92vw',
                    maxWidth: 480, boxShadow: '0 12px 48px rgba(0,0,0,0.18)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Zap size={17} color="#5B35A0" />
            {gq.title || 'Generate Questions with AI'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '0.2rem' }}>
            <X size={18} />
          </button>
        </div>

        {/* Question types */}
        <div style={{ marginBottom: '1.1rem' }}>
          <p style={{ margin: '0 0 0.55rem', fontSize: '0.8rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {gq.questionTypes || 'Question types'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }}>
            {ALL_TYPES.map(({ id, label }) => {
              const on = types.includes(id);
              return (
                <button key={id} onClick={() => toggleType(id)}
                  style={{ padding: '0.55rem 0.75rem', borderRadius: 9, border: `1.5px solid ${on ? '#5B35A0' : '#e0e0e0'}`,
                           background: on ? '#f3f0ff' : '#fafafa', color: on ? '#5B35A0' : '#555',
                           cursor: 'pointer', fontSize: '0.83rem', fontWeight: on ? 600 : 400,
                           display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'all 0.15s' }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, border: `2px solid ${on ? '#5B35A0' : '#ccc'}`,
                                 background: on ? '#5B35A0' : 'transparent', flexShrink: 0,
                                 display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {on && <span style={{ color: '#fff', fontSize: 9, fontWeight: 900 }}>✓</span>}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Count + Difficulty row */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.1rem' }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {gq.count || 'How many'}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="range" min={2} max={12} value={count}
                onChange={e => setCount(+e.target.value)}
                style={{ flex: 1, accentColor: '#5B35A0' }} />
              <span style={{ fontWeight: 700, color: '#5B35A0', minWidth: 20, textAlign: 'right' }}>{count}</span>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {gq.difficulty || 'Difficulty'}
            </p>
            <div style={{ display: 'flex', gap: '0.3rem' }}>
              {['easy', 'mixed', 'hard'].map(d => (
                <button key={d} onClick={() => setDiff(d)}
                  style={{ flex: 1, padding: '0.35rem 0.2rem', borderRadius: 7, border: `1.5px solid ${diff === d ? '#5B35A0' : '#e0e0e0'}`,
                           background: diff === d ? '#5B35A0' : '#fafafa', color: diff === d ? '#fff' : '#555',
                           cursor: 'pointer', fontSize: '0.75rem', fontWeight: diff === d ? 700 : 400 }}>
                  {d === 'easy' ? (gq.easy || 'Easy') : d === 'mixed' ? (gq.mixed || 'Mixed') : (gq.hard || 'Hard')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sample questions */}
        <div style={{ marginBottom: '1.25rem' }}>
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.8rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {gq.sampleLabel || 'Style example'} <span style={{ fontWeight: 400, textTransform: 'none', color: '#aaa' }}>({gq.optional || 'optional'})</span>
          </p>
          <textarea
            value={sample}
            onChange={e => setSample(e.target.value)}
            placeholder={gq.samplePlaceholder || 'Paste 1–2 of your own exam questions here — AI will match the style, depth, and format…'}
            rows={3}
            style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: 9, border: '1.5px solid #e0e0e0',
                     fontSize: '0.83rem', resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                     color: '#333', background: '#fafafa', boxSizing: 'border-box' }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: '1px solid #ddd',
                     background: '#f5f5f5', cursor: 'pointer', fontSize: '0.88rem' }}>
            {tT.cancel || 'Cancel'}
          </button>
          <button onClick={handleSubmit} disabled={types.length === 0}
            style={{ padding: '0.55rem 1.25rem', borderRadius: 8, border: 'none',
                     background: types.length === 0 ? '#ccc' : '#5B35A0', color: '#fff',
                     cursor: types.length === 0 ? 'not-allowed' : 'pointer',
                     fontSize: '0.88rem', fontWeight: 600,
                     display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Zap size={14} />
            {gq.generate || 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuestionModal({ question, onSave, onClose }) {
  const { language } = useLanguage();
  const tT = translations[language]?.studio?.teacher || {};

  const [form, setForm] = useState({
    text: question?.text || '',
    type: question?.type || 'open_ended',
    options: question?.options || ['A) ', 'B) ', 'C) ', 'D) '],
    correct_answer: question?.correct_answer || '',
    model_answer: question?.model_answer || '',
    grading_rubric: question?.grading_rubric || '',
    difficulty: question?.difficulty || 1,
    is_approved: question?.is_approved || false,
  });

  const save = () => onSave(form);

  return (
    <div className="studio-modal-overlay" onClick={onClose}>
      <div className="studio-modal" onClick={e => e.stopPropagation()}>
        <div className="studio-modal-header">
          <h2>{question?.id ? tT.editQuestion || 'Edit Question' : tT.addQuestion || 'Add Question'}</h2>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="form-group">
          <label className="form-label">{tT.questionType || 'Type'}</label>
          <select className="form-select" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            <option value="multiple_choice">{tT.multipleChoice || 'Multiple Choice'}</option>
            <option value="open_ended">{tT.openEnded || 'Open Ended'}</option>
            <option value="numeric">{tT.numeric || 'Numeric'}</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">{tT.questionText || 'Question Text'} *</label>
          <textarea className="form-textarea" rows={3} value={form.text}
            onChange={e => setForm(f => ({ ...f, text: e.target.value }))} />
        </div>

        {form.type === 'multiple_choice' && (
          <>
            <div className="form-group">
              <label className="form-label">{tT.options || 'Answer Options'}</label>
              {form.options.map((opt, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                  <input type="radio" name="correct" checked={form.correct_answer === opt}
                    onChange={() => setForm(f => ({ ...f, correct_answer: opt }))} title={tT.correctAnswer || 'Mark as correct'} />
                  <input className="form-input" value={opt}
                    onChange={e => { const opts = [...form.options]; opts[i] = e.target.value; setForm(f => ({ ...f, options: opts })); }} />
                </div>
              ))}
            </div>
          </>
        )}

        {form.type === 'numeric' && (
          <div className="form-group">
            <label className="form-label">{tT.correctAnswer || 'Correct Answer'}</label>
            <input className="form-input" value={form.correct_answer}
              onChange={e => setForm(f => ({ ...f, correct_answer: e.target.value }))}
              placeholder="e.g. 42.5" />
          </div>
        )}

        {form.type === 'open_ended' && (
          <>
            <div className="form-group">
              <label className="form-label">{tT.modelAnswer || 'Model Answer'}</label>
              <textarea className="form-textarea" rows={3} value={form.model_answer}
                onChange={e => setForm(f => ({ ...f, model_answer: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">{tT.rubric || 'Grading Rubric'}</label>
              <textarea className="form-textarea" rows={2} value={form.grading_rubric}
                onChange={e => setForm(f => ({ ...f, grading_rubric: e.target.value }))} />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label className="form-label">{tT.difficulty || 'Difficulty'} (1–3)</label>
            <select className="form-select" value={form.difficulty}
              onChange={e => setForm(f => ({ ...f, difficulty: parseInt(e.target.value) }))}>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
            <input type="checkbox" id="approved" checked={form.is_approved}
              onChange={e => setForm(f => ({ ...f, is_approved: e.target.checked }))} />
            <label htmlFor="approved" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#444' }}>{tT.approved || 'Approved'}</label>
          </div>
        </div>

        <div className="studio-modal-footer">
          <button className="btn-secondary" onClick={onClose}>{tT.cancel || 'Cancel'}</button>
          <button className="btn-primary" onClick={save}>{tT.saveQuestion || 'Save Question'}</button>
        </div>
      </div>
    </div>
  );
}

function VideoPickerModal({ conceptId, onClose, onAdded, t }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    getMyVideos()
      .then(setVideos)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async (videoId) => {
    setAdding(videoId);
    try {
      await addConceptVideo(conceptId, videoId);
      onAdded();
      onClose();
    } catch (e) {
      alert(e.message);
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="studio-modal-overlay" onClick={onClose}>
      <div className="studio-modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="studio-modal-header">
          <h2>{t.teacher?.pickVideo || 'Pick a Video from Your Library'}</h2>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        {loading ? (
          <div className="loading-center" style={{ padding: '2rem' }}><div className="spinner spinner-sm" /></div>
        ) : videos.length === 0 ? (
          <p style={{ color: '#aaa', textAlign: 'center', padding: '1.5rem' }}>
            {t.teacher?.noVideosInLibrary || 'No completed videos found in your library'}
          </p>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {videos.map(v => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem', borderRadius: 8, border: '1px solid #e8ecf0' }}>
                {v.thumbnail_url ? (
                  <img src={v.thumbnail_url} alt="" style={{ width: 72, height: 42, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 72, height: 42, borderRadius: 6, background: '#f0f0f0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Video size={18} color="#bbb" />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.title || `Video #${v.id}`}
                  </div>
                  {v.duration_seconds && (
                    <div style={{ fontSize: '0.75rem', color: '#999' }}>
                      {Math.floor(v.duration_seconds / 60)}:{String(v.duration_seconds % 60).padStart(2, '0')}
                    </div>
                  )}
                </div>
                <button className="btn-primary" style={{ padding: '0.35rem 0.8rem', fontSize: '0.8rem', flexShrink: 0 }}
                  onClick={() => handleAdd(v.id)} disabled={adding === v.id}>
                  {adding === v.id ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : (t.teacher?.addToConceptBtn || 'Add')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TranscriptModal({ concept, onClose, onGenerate, fetchTranscript }) {
  const { language } = useLanguage();
  const tT = translations[language]?.studio?.teacher || {};
  const [phase, setPhase] = useState('loading'); // loading | ready | generating
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(true);

  // D — Improve section
  const [improving, setImproving] = useState(false);
  const [improveInstruction, setImproveInstruction] = useState('');
  const [showImproveBar, setShowImproveBar] = useState(false);
  const [selection, setSelection] = useState({ start: 0, end: 0, text: '' });

  // G — Add example
  const [showExampleBar, setShowExampleBar] = useState(false);
  const [exampleTopic, setExampleTopic] = useState('');
  const [addingExample, setAddingExample] = useState(false);

  const textareaRef = useRef(null);
  const previewRef = useRef(null);

  // fetchTranscript prop lets ExtraVideoModal inject its own fetcher
  const doFetch = fetchTranscript || (() => previewConceptTranscript(concept.id));

  useEffect(() => {
    doFetch()
      .then(data => { setTranscript(data.transcript_markdown || ''); setPhase('ready'); })
      .catch(e => { setError(e.message); setPhase('ready'); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegenerate = () => {
    setPhase('loading');
    setError('');
    setShowImproveBar(false);
    setShowExampleBar(false);
    doFetch()
      .then(data => { setTranscript(data.transcript_markdown || ''); setPhase('ready'); })
      .catch(e => { setError(e.message); setPhase('ready'); });
  };

  const handleGenerate = () => {
    setPhase('generating');
    onGenerate(transcript);
  };

  // Capture textarea selection for D (Improve)
  const handleTextareaSelect = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = transcript.slice(start, end).trim();
    if (text.length > 10) {
      setSelection({ start, end, text });
      setShowImproveBar(true);
      setShowExampleBar(false);
    }
  };

  // D — Improve selected section
  const handleImprove = async () => {
    if (!selection.text) return;
    setImproving(true);
    try {
      const data = await improveTranscriptSection(concept.id, {
        selected_text: selection.text,
        full_transcript: transcript,
        instruction: improveInstruction || undefined,
      });
      const improved = data.improved_text;
      setTranscript(prev =>
        prev.slice(0, selection.start) + improved + prev.slice(selection.end)
      );
      setShowImproveBar(false);
      setImproveInstruction('');
      setSelection({ start: 0, end: 0, text: '' });
    } catch (e) {
      alert('Improve failed: ' + e.message);
    } finally {
      setImproving(false);
    }
  };

  // G — Add example: smart insert before Comparison/Summary sections if no cursor
  const getSmartInsertPosition = (text, cursorPos) => {
    if (cursorPos > 0) return cursorPos;
    // Try to insert before common closing sections
    const closingMarkers = [
      /^##\s*(Comparison|Vertailu|Jämförelse)/im,
      /^##\s*(Summary|Yhteenveto|Sammanfattning)/im,
      /^##\s*(Conclusion|Johtopäätös|Slutsats)/im,
      /^##\s*(Review|Kertaus|Repetition)/im,
      /^##\s*(Recap|Kooste)/im,
    ];
    for (const marker of closingMarkers) {
      const match = marker.exec(text);
      if (match) return match.index;
    }
    return text.length; // fallback: end
  };

  const handleAddExample = async () => {
    if (!exampleTopic.trim()) return;
    setAddingExample(true);
    try {
      const data = await addExampleToTranscript(concept.id, {
        topic: exampleTopic,
        full_transcript: transcript,
      });
      const ta = textareaRef.current;
      const cursorPos = ta ? ta.selectionStart : 0;
      const insertAt = getSmartInsertPosition(transcript, cursorPos);
      const insertion = '\n\n' + data.example_text + '\n\n';
      setTranscript(prev => {
        const updated = prev.slice(0, insertAt) + insertion + prev.slice(insertAt);
        // Scroll preview to insertion point after React re-renders
        const fraction = insertAt / Math.max(prev.length, 1);
        setTimeout(() => {
          if (previewRef.current) {
            previewRef.current.scrollTop = fraction * previewRef.current.scrollHeight;
          }
        }, 80);
        return updated;
      });
      setShowExampleBar(false);
      setExampleTopic('');
    } catch (e) {
      alert('Add example failed: ' + e.message);
    } finally {
      setAddingExample(false);
    }
  };

  const wordCount = transcript.trim().split(/\s+/).filter(Boolean).length;
  const estMinutes = Math.round(wordCount / 130) || 1;

  return (
    <div className="studio-modal-overlay" onClick={onClose}>
      <div
        className="studio-modal"
        style={{ maxWidth: 960, width: '95vw', display: 'flex', flexDirection: 'column', maxHeight: '92vh', overflow: 'hidden', padding: 0 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="studio-modal-header" style={{ padding: '1.25rem 1.75rem 1rem', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>{tT.reviewTranscript || 'Review Transcript'}</h2>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#888' }}>{concept.title}</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {phase === 'ready' && !error && (
              <button
                className="btn-secondary"
                style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
                onClick={() => setShowPreview(p => !p)}
                title={showPreview ? (tT.hidePreview || 'Hide preview') : (tT.preview || 'Preview')}
              >
                {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
                {showPreview ? (tT.hidePreview || 'Hide preview') : (tT.preview || 'Preview')}
              </button>
            )}
            <button className="btn-icon" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        {/* AI toolbar — Improve (D) */}
        {showImproveBar && phase === 'ready' && (
          <div style={{ padding: '0.6rem 1.75rem', background: '#f3f0ff', borderTop: '1px solid #e4deff', borderBottom: '1px solid #e4deff', display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
            <Sparkles size={14} color="#5B35A0" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '0.78rem', color: '#5B35A0', fontWeight: 600, flexShrink: 0 }}>{tT.improveSelection || 'Improve selection:'}</span>
            <input
              style={{ flex: 1, minWidth: 160, padding: '0.3rem 0.6rem', fontSize: '0.82rem', borderRadius: 7, border: '1.5px solid #c9b8f0', outline: 'none' }}
              placeholder={tT.improvePlaceholder || 'e.g. simplify for students, expand with more detail…'}
              value={improveInstruction}
              onChange={e => setImproveInstruction(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleImprove()}
            />
            <button className="btn-primary" style={{ padding: '0.3rem 0.8rem', fontSize: '0.8rem' }} onClick={handleImprove} disabled={improving}>
              {improving ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={13} />}
              {improving ? (tT.improving || 'Improving…') : (tT.improve || 'Improve')}
            </button>
            <button className="btn-icon" onClick={() => setShowImproveBar(false)}><X size={14} /></button>
          </div>
        )}

        {/* AI toolbar — Add Example (G) */}
        {showExampleBar && phase === 'ready' && (
          <div style={{ padding: '0.6rem 1.75rem', background: '#f0fffe', borderTop: '1px solid #b2f0ec', borderBottom: '1px solid #b2f0ec', display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
            <FlaskConical size={14} color="#0DBFB0" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '0.78rem', color: '#0DBFB0', fontWeight: 600, flexShrink: 0 }}>{tT.addExampleAbout || 'Add example about:'}</span>
            <input
              style={{ flex: 1, minWidth: 160, padding: '0.3rem 0.6rem', fontSize: '0.82rem', borderRadius: 7, border: '1.5px solid #7de8e3', outline: 'none' }}
              placeholder={`e.g. ${concept.title} with numbers…`}
              value={exampleTopic}
              onChange={e => setExampleTopic(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddExample()}
            />
            <button
              style={{ padding: '0.3rem 0.8rem', fontSize: '0.8rem', background: '#0DBFB0', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
              onClick={handleAddExample}
              disabled={addingExample || !exampleTopic.trim()}
            >
              {addingExample ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={13} />}
              {addingExample ? (tT.adding || 'Adding…') : (tT.add || 'Add')}
            </button>
            <button className="btn-icon" onClick={() => setShowExampleBar(false)}><X size={14} /></button>
          </div>
        )}

        {/* Main content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.75rem' }}>
          {phase === 'loading' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 0', gap: '0.75rem', color: '#5B35A0' }}>
              <Loader size={28} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ margin: 0, fontSize: '0.88rem' }}>{tT.aiWritingTranscript || 'AI is writing the transcript…'}</p>
            </div>
          ) : error ? (
            <div style={{ color: '#c62828', background: '#fff0f0', padding: '0.85rem 1rem', borderRadius: 10, marginBottom: '1rem', fontSize: '0.88rem' }}>
              ❌ {error}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr', gap: '1rem', alignItems: 'start' }}>
              {/* Left — Editor */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tT.editor || 'Editor'}</span>
                  <span style={{ fontSize: '0.72rem', color: '#bbb' }}>{wordCount} {(tT.wordsMin || 'words · ~{min} min video').replace('{min}', estMinutes)}</span>
                </div>
                <textarea
                  ref={textareaRef}
                  style={{
                    width: '100%', boxSizing: 'border-box', minHeight: 360,
                    padding: '0.85rem 1rem', fontSize: '0.84rem', lineHeight: 1.65,
                    borderRadius: 10, border: '1.5px solid #e0e3ef',
                    fontFamily: 'monospace', resize: 'vertical', outline: 'none',
                    background: '#fafbff',
                  }}
                  value={transcript}
                  onChange={e => setTranscript(e.target.value)}
                  onMouseUp={handleTextareaSelect}
                  onKeyUp={handleTextareaSelect}
                />
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem', color: '#bbb' }}>
                  {tT.selectTextHint || 'Select text → Improve with AI · Place cursor → Add Example'}
                </p>
              </div>

              {/* Right — Preview */}
              {showPreview && (
                <div>
                  <div style={{ marginBottom: '0.4rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tT.preview || 'Preview'}</span>
                  </div>
                  <div ref={previewRef} style={{
                    minHeight: 360, padding: '0.85rem 1rem', borderRadius: 10,
                    border: '1.5px solid #e8edff', background: 'white',
                    fontSize: '0.88rem', lineHeight: 1.7, color: '#1a1a2e',
                    overflowY: 'auto', maxHeight: 400,
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                      {transcript || '*Nothing to preview yet…*'}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="studio-modal-footer" style={{ padding: '1rem 1.75rem', flexShrink: 0, flexWrap: 'wrap', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={onClose}>{tT.cancel || 'Cancel'}</button>
          {phase === 'ready' && !error && (
            <>
              <button className="btn-secondary" onClick={handleRegenerate}>
                <RefreshCw size={14} /> {tT.regenerate || 'Regenerate'}
              </button>
              <button
                className="btn-secondary"
                style={{ borderColor: '#0DBFB0', color: '#0DBFB0' }}
                onClick={() => { setShowExampleBar(p => !p); setShowImproveBar(false); }}
              >
                <FlaskConical size={14} /> {tT.addExample || 'Add Example'}
              </button>
            </>
          )}
          <button className="btn-primary"
            onClick={handleGenerate}
            disabled={phase !== 'ready' || !transcript.trim()}>
            {phase === 'generating'
              ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> {tT.startingRender || 'Starting render…'}</>
              : <><Video size={14} /> {tT.generateVideo || 'Generate Video'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExtraVideoModal({ concept, onClose, onGenerate }) {
  const { language } = useLanguage();
  const tT = translations[language]?.studio?.teacher || {};

  // Step 1: enter focus prompt
  // Step 2: transcript modal (reuses TranscriptModal with custom fetcher)
  const [customPrompt, setCustomPrompt] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);

  const handlePreview = () => {
    if (!customPrompt.trim()) return;
    setShowTranscript(true);
  };

  if (showTranscript) {
    return (
      <TranscriptModal
        concept={concept}
        onClose={onClose}
        fetchTranscript={() => previewExtraTranscript(concept.id, { custom_prompt: customPrompt })}
        onGenerate={(transcript) => onGenerate(customPrompt, transcript)}
      />
    );
  }

  return (
    <div className="studio-modal-overlay" onClick={onClose}>
      <div className="studio-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="studio-modal-header">
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>{tT.addAnotherVideo || 'Add Another Video'}</h2>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#888' }}>{concept.title}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="form-group">
          <label className="form-label">{tT.extraVideoLabel || 'What should this video specifically cover?'} *</label>
          <textarea
            className="form-textarea"
            rows={4}
            placeholder={tT.extraVideoPlaceholder || `e.g. "Show a worked example with numbers"\n"Explain the formula derivation step by step"\n"Focus on common mistakes students make"`}
            value={customPrompt}
            onChange={e => setCustomPrompt(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && e.ctrlKey && handlePreview()}
          />
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.78rem', color: '#aaa' }}>
            {tT.extraVideoHint || 'Be specific — this tells AI exactly what angle to take for this additional video.'}
          </p>
        </div>

        <div className="studio-modal-footer">
          <button className="btn-secondary" onClick={onClose}>{tT.cancel || 'Cancel'}</button>
          <button className="btn-primary" onClick={handlePreview} disabled={!customPrompt.trim()}>
            <FileText size={14} /> {tT.previewTranscriptBtn || 'Preview Transcript & Generate Video'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Teacher Selection Popup ────────────────────────────────────────────────
// ─── AI Message Row ──────────────────────────────────────────────────────────
function MessageRow({ msg, concepts, tT, language, courseId, onOpenAssignment }) {
  const [conceptId, setConceptId] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [actionDone, setActionDone] = useState({});

  const questions = msg.metadata?.questions || [];

  if (msg.role === 'user') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
        <div style={{ maxWidth: '82%', background: '#5B35A0', color: 'white', borderRadius: '18px 18px 4px 18px', padding: '0.65rem 1rem', fontSize: '0.88rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {msg.content}
        </div>
      </div>
    );
  }

  const handleGenerateVideo = async () => {
    if (!conceptId || actionLoading) return;
    setActionLoading('video');
    try {
      const res = await generateVideoFromPassage(courseId, {
        passage_text: msg.content.slice(0, 2000),
        language: language || 'en',
      });
      await addConceptVideo(conceptId, res.video_id);
      setActionDone(prev => ({ ...prev, video: true }));
    } catch (e) {
      alert('Video generation failed: ' + (e.message || ''));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateAssignment = async () => {
    if (!conceptId || !questions.length || actionLoading) return;
    setActionLoading('assignment');
    try {
      const ids = [];
      for (const q of questions) {
        const created = await createQuestion({
          concept_id: conceptId,
          course_id: courseId,
          type: q.type || 'open_ended',
          text: q.text,
          options: q.options || null,
          correct_answer: q.correct_answer || null,
          model_answer: q.model_answer || null,
          is_approved: true,
        });
        if (created?.id) ids.push(created.id);
      }
      setActionDone(prev => ({ ...prev, assignment: true }));
      onOpenAssignment({ course_id: courseId, question_ids: ids, title: 'AI Quiz', type: questions.length >= 4 ? 'test' : 'assignment' });
    } catch (e) {
      alert('Failed: ' + (e.message || ''));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ background: '#f5f3ff', borderRadius: '4px 18px 18px 18px', padding: '0.75rem 1rem', fontSize: '0.88rem', lineHeight: 1.7, color: '#1a1a2e' }}>
        <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{msg.content}</ReactMarkdown>
      </div>
      <div style={{ marginTop: 3, padding: '0.5rem 0.85rem', background: 'white', border: '1px solid #e8edf5', borderTop: 'none', borderRadius: '0 0 12px 12px', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={conceptId} onChange={e => setConceptId(e.target.value)}
          style={{ flex: 1, minWidth: 130, borderRadius: 7, border: '1px solid #e0d6f5', padding: '3px 7px', fontSize: '0.78rem', color: conceptId ? '#1a1a2e' : '#999', outline: 'none', background: 'white' }}>
          <option value="">{tT.pickConceptFirst || 'Pick concept…'}</option>
          {concepts.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        {actionDone.video ? (
          <span style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
            <Check size={12} /> {tT.videoQueued || 'Video queued!'}
          </span>
        ) : (
          <button onClick={handleGenerateVideo} disabled={!conceptId || !!actionLoading}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 7, border: 'none', background: conceptId && !actionLoading ? 'linear-gradient(135deg,#5B35A0,#667eea)' : '#e5e7eb', color: conceptId && !actionLoading ? 'white' : '#9ca3af', fontSize: '0.75rem', fontWeight: 700, cursor: conceptId && !actionLoading ? 'pointer' : 'default', transition: 'all 0.15s' }}>
            {actionLoading === 'video' ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Video size={11} />}
            {tT.actionVideo || 'Generate Video'}
          </button>
        )}
        {questions.length > 0 && (
          actionDone.assignment ? (
            <span style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
              <Check size={12} /> {tT.questionsCreated || 'Questions created!'}
            </span>
          ) : (
            <button onClick={handleCreateAssignment} disabled={!conceptId || !!actionLoading}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 7, border: 'none', background: conceptId && !actionLoading ? 'linear-gradient(135deg,#d97706,#f59e0b)' : '#e5e7eb', color: conceptId && !actionLoading ? 'white' : '#9ca3af', fontSize: '0.75rem', fontWeight: 700, cursor: conceptId && !actionLoading ? 'pointer' : 'default', transition: 'all 0.15s' }}>
              {actionLoading === 'assignment' ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <ClipboardList size={11} />}
              {tT.pushToAssignment || 'Create Assignment'} ({questions.length})
            </button>
          )
        )}
      </div>
    </div>
  );
}

// ─── Teacher Chat Panel ───────────────────────────────────────────────────────
function TeacherChatPanel({ courseId, course, tT, language, initialText, onClose, onOpenAssignment }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(initialText || '');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const concepts = course?.concepts || [];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    getTeacherChatHistory(courseId)
      .then(data => setMessages(data.messages || []))
      .catch(() => setMessages([]))
      .finally(() => setHistoryLoading(false));
  }, [courseId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (!historyLoading) inputRef.current?.focus();
  }, [historyLoading]);

  const send = async (text) => {
    const msg = (typeof text === 'string' ? text : input).trim();
    if (!msg || loading) return;
    setMessages(prev => [...prev, { id: `u${Date.now()}`, role: 'user', content: msg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await teacherChat({
        course_id: courseId,
        message: msg,
        messages: messages.slice(-12).map(m => ({ role: m.role, content: m.content })),
        language,
      });
      setMessages(prev => [...prev, {
        id: `a${Date.now()}`,
        role: 'assistant',
        content: res.reply,
        metadata: { suggestions: res.suggestions || [], questions: res.questions || [] },
      }]);
    } catch (e) {
      setMessages(prev => [...prev, { id: `e${Date.now()}`, role: 'assistant', content: `Error: ${e.message}`, metadata: {} }]);
    } finally {
      setLoading(false);
    }
  };

  const lastSuggestions = [...messages].reverse().find(m => m.role === 'assistant')?.metadata?.suggestions || [];
  const STARTERS = [
    tT.starterTeach || 'How do I teach this topic step by step?',
    tT.starterQuiz || 'Create a quiz for my students',
    tT.starterMisconceptions || 'What misconceptions will students have?',
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10001, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e8edf5', display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, background: 'white' }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5B35A0', padding: 4, display: 'flex', alignItems: 'center' }}>
          <ChevronLeft size={22} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 9, height: 9, borderRadius: '50%', background: 'linear-gradient(135deg,#5B35A0,#667eea)', flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>
            {tT.teachingAssistant || 'Teaching Assistant'}
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '0.75rem', color: '#bbb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
          {course?.title}
        </span>
      </div>

      {/* Messages area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1rem 0.5rem', display: 'flex', flexDirection: 'column' }}>
        {historyLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <Loader size={22} style={{ animation: 'spin 1s linear infinite', color: '#5B35A0' }} />
          </div>
        ) : messages.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem 1.5rem' }}>
            <Sparkles size={34} style={{ color: '#e0d6f5', marginBottom: '1rem' }} />
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.9rem', color: '#aaa', lineHeight: 1.6, maxWidth: 320 }}>
              {tT.chatPlaceholder || 'Ask me anything about teaching this course — videos, assignments, teaching strategies.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', width: '100%', maxWidth: 340 }}>
              {STARTERS.map(s => (
                <button key={s} onClick={() => send(s)}
                  style={{ padding: '8px 16px', borderRadius: 20, border: '1.5px solid #e0d6f5', background: 'white', color: '#5B35A0', fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map(msg => (
              <MessageRow key={msg.id || msg.created_at}
                msg={msg} concepts={concepts} tT={tT}
                language={language} courseId={courseId}
                onOpenAssignment={onOpenAssignment}
              />
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: 5, padding: '0.6rem 0.9rem', alignSelf: 'flex-start', background: '#f5f3ff', borderRadius: '4px 18px 18px 18px', marginBottom: '0.75rem' }}>
                {[0, 1, 2].map(i => (
                  <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#5B35A0', opacity: 0.5, animation: `bounce 1s ${i * 0.2}s infinite` }} />
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Suggestion chips */}
      {lastSuggestions.length > 0 && !loading && (
        <div style={{ padding: '0.5rem 1rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', borderTop: '1px solid #f0f0f0', background: '#fafafa', flexShrink: 0 }}>
          {lastSuggestions.map((s, i) => (
            <button key={i} onClick={() => send(s)}
              style={{ padding: '4px 12px', borderRadius: 20, border: '1.5px solid #e0d6f5', background: 'white', color: '#5B35A0', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e8edf5', display: 'flex', gap: '0.5rem', background: 'white', flexShrink: 0 }}>
        <input ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={tT.askFollowUp || 'Ask anything about your course…'}
          style={{ flex: 1, borderRadius: 24, border: '1.5px solid #e0d6f5', padding: '0.65rem 1.1rem', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit' }}
        />
        <button onClick={() => send()} disabled={!input.trim() || loading}
          style={{ background: '#5B35A0', border: 'none', borderRadius: '50%', width: 44, height: 44, cursor: !input.trim() || loading ? 'default' : 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: !input.trim() || loading ? 0.5 : 1 }}>
          {loading ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}


// ─── Teacher PDF Viewer ─────────────────────────────────────────────────────
function TeacherPdfViewer({ courseId, course, tT, onOpenChat }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfWidth, setPdfWidth] = useState(500);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [selectionMode, setSelectionMode] = useState('text');
  const [selectionText, setSelectionText] = useState('');
  const [floatBtn, setFloatBtn] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [dragRect, setDragRect] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [regionCapture, setRegionCapture] = useState(null); // {dataUrl}

  const pdfContainerRef = useRef(null);
  const pageWrapRef = useRef(null);

  useEffect(() => {
    if (!course?.syllabus_r2_url) return;
    setPdfLoading(true);
    setPdfError(false);
    let objectUrl = null;
    fetchCoursePdfBlob(courseId)
      .then(blob => { objectUrl = URL.createObjectURL(blob); setPdfBlobUrl(objectUrl); })
      .catch(() => setPdfError(true))
      .finally(() => setPdfLoading(false));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [courseId, course?.syllabus_r2_url]);

  useEffect(() => {
    if (!pdfContainerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const w = Math.floor(e.contentRect.width) - 32;
        if (w > 100) setPdfWidth(w);
      }
    });
    ro.observe(pdfContainerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (selectionMode !== 'text') return;
    const handle = () => {
      const sel = window.getSelection();
      const text = sel?.toString().trim();
      if (!text || text.length < 10 || !pdfContainerRef.current?.contains(sel.anchorNode)) { setFloatBtn(null); return; }
      try {
        const rect = sel.getRangeAt(0).getBoundingClientRect();
        setFloatBtn({ x: rect.left + rect.width / 2, y: rect.bottom + 8 });
        setSelectionText(text);
      } catch { setFloatBtn(null); }
    };
    document.addEventListener('selectionchange', handle);
    return () => document.removeEventListener('selectionchange', handle);
  }, [selectionMode]);

  const cropCanvas = useCallback((rect) => {
    if (!pageWrapRef.current) return;
    const canvas = pageWrapRef.current.querySelector('canvas');
    if (!canvas) return;

    // Canvas may be centred inside the wrapper — compute its offset within the wrapper
    const canvasRect = canvas.getBoundingClientRect();
    const wrapRect = pageWrapRef.current.getBoundingClientRect();
    const offsetX = canvasRect.left - wrapRect.left;
    const offsetY = canvasRect.top - wrapRect.top;

    // Scale: actual canvas pixel size vs CSS display size
    const scaleX = canvas.width / canvasRect.width;
    const scaleY = canvas.height / canvasRect.height;

    const srcX = Math.round((rect.x - offsetX) * scaleX);
    const srcY = Math.round((rect.y - offsetY) * scaleY);
    const srcW = Math.max(1, Math.round(rect.w * scaleX));
    const srcH = Math.max(1, Math.round(rect.h * scaleY));

    const out = document.createElement('canvas');
    out.width = srcW;
    out.height = srcH;
    out.getContext('2d').drawImage(canvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
    const dataUrl = out.toDataURL('image/png');
    setRegionCapture({ dataUrl });
  }, []);

  const onPageMouseDown = useCallback((e) => {
    if (selectionMode !== 'region') return;
    e.preventDefault();
    const pr = pageWrapRef.current.getBoundingClientRect();
    setDragStart({ x: e.clientX - pr.left, y: e.clientY - pr.top });
    setDragRect(null); setIsDragging(true); setRegionCapture(null);
  }, [selectionMode]);

  const onPageMouseMove = useCallback((e) => {
    if (!isDragging || !dragStart) return;
    const pr = pageWrapRef.current.getBoundingClientRect();
    const cx = e.clientX - pr.left, cy = e.clientY - pr.top;
    setDragRect({ x: Math.min(dragStart.x, cx), y: Math.min(dragStart.y, cy), w: Math.abs(cx - dragStart.x), h: Math.abs(cy - dragStart.y) });
  }, [isDragging, dragStart]);

  const onPageMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (!dragRect || dragRect.w < 10 || dragRect.h < 10) { setDragRect(null); return; }
    cropCanvas(dragRect);
  }, [isDragging, dragRect, cropCanvas]);

  const onPageTouchStart = useCallback((e) => {
    if (selectionMode !== 'region') return;
    const t = e.touches[0];
    const pr = pageWrapRef.current.getBoundingClientRect();
    setDragStart({ x: t.clientX - pr.left, y: t.clientY - pr.top });
    setDragRect(null); setIsDragging(true); setRegionCapture(null);
  }, [selectionMode]);

  const onPageTouchMove = useCallback((e) => {
    if (!isDragging || !dragStart) return;
    e.preventDefault();
    const t = e.touches[0];
    const pr = pageWrapRef.current.getBoundingClientRect();
    const cx = t.clientX - pr.left, cy = t.clientY - pr.top;
    setDragRect({ x: Math.min(dragStart.x, cx), y: Math.min(dragStart.y, cy), w: Math.abs(cx - dragStart.x), h: Math.abs(cy - dragStart.y) });
  }, [isDragging, dragStart]);

  const onPageTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (!dragRect || dragRect.w < 10 || dragRect.h < 10) { setDragRect(null); return; }
    cropCanvas(dragRect);
  }, [isDragging, dragRect, cropCanvas]);

  if (!course?.syllabus_r2_url) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#aaa' }}>
        <FileText size={36} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
        <p style={{ margin: 0, fontSize: '0.88rem' }}>{tT.noPdfUploaded || 'No syllabus PDF uploaded for this course.'}</p>
      </div>
    );
  }

  const hasPdf = Boolean(pdfBlobUrl) && !pdfError;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }} ref={pdfContainerRef}>
      {/* Mode toggle */}
      {hasPdf && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem' }}>
          <span style={{ fontSize: '0.78rem', color: '#888' }}>
            {selectionMode === 'text' ? (tT.selectTextPrompt || 'Select text to ask the teaching assistant') : (tT.selectRegionPrompt || 'Drag to capture a formula or diagram')}
          </span>
          <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e0d6f5' }}>
            {['text', 'region'].map(mode => (
              <button key={mode} onClick={() => { setSelectionMode(mode); setDragRect(null); setRegionCapture(null); setFloatBtn(null); }}
                style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: selectionMode === mode ? '#5B35A0' : 'white', color: selectionMode === mode ? 'white' : '#5B35A0' }}>
                {mode === 'text' ? 'Text' : 'Region'}
              </button>
            ))}
          </div>
        </div>
      )}

      {pdfLoading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading PDF…
        </div>
      ) : pdfError ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#e53935', fontSize: '0.85rem' }}>Could not load PDF.</div>
      ) : hasPdf ? (
        <>
          <div
            ref={pageWrapRef}
            style={{ position: 'relative', background: '#f4f4f4', borderRadius: 10, border: '1px solid #e8edf5', display: 'flex', justifyContent: 'center', cursor: selectionMode === 'region' ? 'crosshair' : 'text', userSelect: selectionMode === 'region' ? 'none' : 'text' }}
            onMouseDown={onPageMouseDown} onMouseMove={onPageMouseMove} onMouseUp={onPageMouseUp}
            onTouchStart={onPageTouchStart} onTouchMove={onPageTouchMove} onTouchEnd={onPageTouchEnd}
          >
            <Document
              file={pdfBlobUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={() => setPdfError(true)}
              loading={<div style={{ padding: '3rem', color: '#888', display: 'flex', alignItems: 'center', gap: 8 }}><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Rendering…</div>}
            >
              <Page pageNumber={pageNumber} width={pdfWidth} renderTextLayer={selectionMode === 'text'} renderAnnotationLayer={false} />
            </Document>
            {selectionMode === 'region' && dragRect && (
              <div style={{ position: 'absolute', left: dragRect.x, top: dragRect.y, width: dragRect.w, height: dragRect.h, border: '2px solid #5B35A0', background: 'rgba(91,53,160,0.12)', pointerEvents: 'none' }} />
            )}
          </div>

          {numPages && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem' }}>
              <button disabled={pageNumber <= 1} onClick={() => { setPageNumber(p => p - 1); setDragRect(null); setRegionCapture(null); }}
                style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', cursor: pageNumber <= 1 ? 'default' : 'pointer', opacity: pageNumber <= 1 ? 0.35 : 1, display: 'flex', alignItems: 'center' }}>
                <ChevronLeft size={15} />
              </button>
              <span style={{ fontSize: '0.85rem', color: '#555', minWidth: 60, textAlign: 'center' }}>{pageNumber} / {numPages}</span>
              <button disabled={pageNumber >= numPages} onClick={() => { setPageNumber(p => p + 1); setDragRect(null); setRegionCapture(null); }}
                style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', cursor: pageNumber >= numPages ? 'default' : 'pointer', opacity: pageNumber >= numPages ? 0.35 : 1, display: 'flex', alignItems: 'center' }}>
                <ChevronRight size={15} />
              </button>
            </div>
          )}
        </>
      ) : null}

      {/* Floating "Ask AI" button — opens chat with selected text pre-filled */}
      {floatBtn && (
        <button
          onMouseDown={e => {
            e.preventDefault();
            onOpenChat(selectionText);
            setFloatBtn(null);
          }}
          style={{ position: 'fixed', left: floatBtn.x, top: floatBtn.y, transform: 'translateX(-50%)', zIndex: 9999, background: 'linear-gradient(135deg,#5B35A0,#667eea)', color: 'white', border: 'none', borderRadius: 20, padding: '6px 16px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(91,53,160,0.4)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}
        >
          <MessageSquare size={13} /> {tT.askTeachingAssistant || 'Ask teaching assistant'}
        </button>
      )}

      {/* Region capture preview + "Send to Chat" */}
      {regionCapture && (
        <div style={{ marginTop: '0.5rem', border: '1.5px solid #e0d6f5', borderRadius: 10, padding: '0.75rem', background: '#fafaff', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <img src={regionCapture.dataUrl} alt="captured region" style={{ borderRadius: 6, maxHeight: 200, objectFit: 'contain', background: '#f4f4f4', width: '100%' }} />
          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
            <button onClick={() => setRegionCapture(null)}
              style={{ padding: '4px 12px', borderRadius: 8, border: '1px solid #e0d6f5', background: 'white', color: '#888', fontSize: '0.78rem', cursor: 'pointer' }}>
              Discard
            </button>
            <button onClick={() => { onOpenChat(tT.askTeachingAssistant || 'Here is a selected region from my course PDF. Please help me teach this content.'); setRegionCapture(null); }}
              style={{ padding: '4px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#5B35A0,#667eea)', color: 'white', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <MessageSquare size={12} /> {tT.askTeachingAssistant || 'Send to AI Chat'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CourseEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = React.useMemo(() => translations[language]?.studio || {}, [language]);
  const tT = React.useMemo(() => t.teacher || {}, [t]);

  const [course, setCourse] = useState(null);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [generatingQ, setGeneratingQ] = useState(false);
  const [showGenQModal, setShowGenQModal] = useState(false);
  const [generatingV, setGeneratingV] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [modalQuestion, setModalQuestion] = useState(null);
  const [showQModal, setShowQModal] = useState(false);
  const [showVideoPicker, setShowVideoPicker] = useState(false);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [showExtraVideoModal, setShowExtraVideoModal] = useState(false);
  const [showTranscriptView, setShowTranscriptView] = useState(false);
  const [transcriptViewVideoId, setTranscriptViewVideoId] = useState(null); // null = primary concept
  const [playingVideoId, setPlayingVideoId] = useState(null); // tracks which video is playing (primary='primary', linked=lv.id)
  const [toast, setToast] = useState('');
  const [expandedQ, setExpandedQ] = useState(null);
  const [mobilePanel, setMobilePanel] = useState('concepts'); // 'concepts' | 'detail'
  const selectedConceptIdRef = useRef(null);
  const pollRef = useRef(null);
  const detailPanelRef = useRef(null);

  // Concept management
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null); // concept to confirm-delete
  const [bulkDeletePending, setBulkDeletePending] = useState(false);

  // PDF viewer + teaching assistant chat
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [showTeacherChat, setShowTeacherChat] = useState(false);
  const [chatInitialText, setChatInitialText] = useState('');

  // Source page viewer
  const [sourceData, setSourceData] = useState(null);   // { pages, excerpts, concept_title }
  const [sourceLoading, setSourceLoading] = useState(false);

  // Inline rename
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  // Syllabus delete
  const [syllabusDeleteConfirm, setSyllabusDeleteConfirm] = useState(false);
  const [syllabusDeleting, setSyllabusDeleting] = useState(false);

  // Assignments panel
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [assignmentsExpanded, setAssignmentsExpanded] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [conceptsExpanded, setConceptsExpanded] = useState(true);

  // Page range filter for extract-concepts
  const [pageFrom, setPageFrom] = useState('');
  const [pageTo, setPageTo] = useState('');


  // DnD sensors: pointer with 250ms long-press delay, touch with 300ms
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 8 } }),
  );

  const showToast = (msg, type = '') => {
    setToast({ msg, type });
    setTimeout(() => setToast(''), 3000);
  };

  const handleDeleteConcept = async (concept) => {
    try {
      await deleteConcept(concept.id);
      if (selectedConcept?.id === concept.id) {
        setSelectedConcept(null);
        selectedConceptIdRef.current = null;
      }
      setDeleteConfirm(null);
      showToast('Concept deleted', 'success');
      loadCourse();
    } catch (e) {
      showToast('Delete failed', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeletePending(true);
    try {
      await bulkDeleteConcepts([...selectedIds]);
      if (selectedIds.has(selectedConcept?.id)) {
        setSelectedConcept(null);
        selectedConceptIdRef.current = null;
      }
      setSelectedIds(new Set());
      setSelectMode(false);
      showToast(`${selectedIds.size} concept(s) deleted`, 'success');
      loadCourse();
    } catch (e) {
      showToast('Bulk delete failed', 'error');
    } finally {
      setBulkDeletePending(false);
    }
  };

  const toggleSelectConcept = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };


  const handleSaveRename = async (concept) => {
    const newTitle = renameValue.trim();
    if (!newTitle || newTitle === concept.title) { setRenamingId(null); return; }
    setRenameSaving(true);
    try {
      await updateConcept(concept.id, { title: newTitle });
      setRenamingId(null);
      if (selectedConcept?.id === concept.id) {
        setSelectedConcept(c => ({ ...c, title: newTitle }));
      }
      loadCourse();
    } catch (e) {
      showToast('Rename failed', 'error');
    } finally {
      setRenameSaving(false);
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const concepts = course.concepts;
    const oldIndex = concepts.findIndex(c => c.id === active.id);
    const newIndex = concepts.findIndex(c => c.id === over.id);
    const reordered = arrayMove(concepts, oldIndex, newIndex);
    setCourse(c => ({ ...c, concepts: reordered }));
    try {
      await reorderConcepts(course.id, reordered.map(c => c.id));
    } catch (e) {
      showToast('Reorder failed', 'error');
      setCourse(c => ({ ...c, concepts: concepts }));
    }
  };

  const handleDeleteSyllabus = async () => {
    setSyllabusDeleting(true);
    try {
      await deleteSyllabus(course.id);
      setSyllabusDeleteConfirm(false);
      showToast('Syllabus removed', 'success');
      loadCourse();
    } catch (e) {
      showToast('Failed to remove syllabus', 'error');
    } finally {
      setSyllabusDeleting(false);
    }
  };

  const handleShowSource = async (concept) => {
    setSourceData(null);
    setSourceLoading(true);
    try {
      const data = await getConceptSource(concept.id);
      setSourceData(data);
    } catch (e) {
      showToast('Could not load source pages', 'error');
    } finally {
      setSourceLoading(false);
    }
  };

  const loadCourse = useCallback(async () => {
    try {
      const data = await getCourse(courseId);
      setCourse(data);
      const currentId = selectedConceptIdRef.current;
      if (data.concepts?.length > 0) {
        if (!currentId) {
          // First load — pick first concept
          setSelectedConcept(data.concepts[0]);
          selectedConceptIdRef.current = data.concepts[0].id;
        } else {
          // Refresh — sync selectedConcept with fresh data so video_url/status updates
          const fresh = data.concepts.find(c => c.id === currentId);
          if (fresh) setSelectedConcept(fresh);
        }
      }
    } catch (e) { console.error(e); }
  }, [courseId]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  const loadAssignments = useCallback(async () => {
    try {
      const all = await listAssignments(courseId);
      setCourseAssignments(all);
    } catch (e) { console.error(e); }
  }, [courseId]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  // Open teaching assistant chat (optionally pre-fill with text from PDF selection)
  const handleOpenChat = useCallback((text = '') => {
    setChatInitialText(text || '');
    setShowTeacherChat(true);
  }, []);

  // Called from MessageRow inside TeacherChatPanel when teacher creates an assignment
  const handleOpenAssignment = useCallback(({ course_id, question_ids, title, type }) => {
    setEditingAssignment({ course_id, question_ids, title, type });
    setShowAssignmentForm(true);
    setShowTeacherChat(false);
  }, []);

  // Poll every 6s while primary video or any linked video is still rendering
  useEffect(() => {
    clearInterval(pollRef.current);
    const primaryDone = ['ready', 'completed', 'error'].includes(selectedConcept?.video_status);
    const linkedPending = (selectedConcept?.linked_videos || []).some(
      lv => ['queued', 'processing', 'rendering'].includes(lv.status)
    );
    const shouldPoll = (selectedConcept?.video_id && !primaryDone) || linkedPending;
    if (shouldPoll) {
      pollRef.current = setInterval(() => { loadCourse(); }, 6000);
    }
    return () => clearInterval(pollRef.current);
  }, [selectedConcept?.video_status, selectedConcept?.video_id, selectedConcept?.linked_videos, loadCourse]);

  useEffect(() => {
    if (!selectedConcept?.id) return;
    setLoadingQuestions(true);
    getConceptQuestions(selectedConcept.id)
      .then(setQuestions)
      .catch(console.error)
      .finally(() => setLoadingQuestions(false));
  }, [selectedConcept]);

  const handleExtractConcepts = async () => {
    setExtracting(true);
    const body = {};
    if (pageFrom && parseInt(pageFrom) > 0) body.page_from = parseInt(pageFrom);
    if (pageTo && parseInt(pageTo) > 0) body.page_to = parseInt(pageTo);
    try {
      const res = await extractConcepts(courseId, body);
      showToast(`Extracted ${res.count} concepts`, 'success');
      loadCourse();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setExtracting(false); }
  };


  // Called from TranscriptModal with teacher-approved transcript
  const handleGenerateVideoFromTranscript = async (transcript) => {
    setShowTranscriptModal(false);
    setGeneratingV(true);
    try {
      await generateConceptVideo(selectedConcept.id, { transcript_markdown: transcript });
      showToast('Video generation started', 'success');
      loadCourse();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setGeneratingV(false); }
  };

  // Called from ExtraVideoModal after teacher approves transcript
  const handleGenerateExtraVideo = async (customPrompt, transcriptMarkdown) => {
    try {
      await generateExtraConceptVideo(selectedConcept.id, {
        custom_prompt: customPrompt,
        transcript_markdown: transcriptMarkdown || undefined,
      });
      setShowExtraVideoModal(false);
      showToast(tT.starting || 'Video generation started', 'success');
      loadCourse();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleGenerateQuestions = async (config) => {
    setShowGenQModal(false);
    setGeneratingQ(true);
    try {
      const body = {
        types: config.types,
        count: config.count,
        difficulty: config.difficulty,
        ...(config.sample_questions ? { sample_questions: config.sample_questions } : {}),
      };
      const res = await generateConceptQuestions(selectedConcept.id, body);
      showToast(`Generated ${res.count} questions`, 'success');
      const qs = await getConceptQuestions(selectedConcept.id);
      setQuestions(qs);
    } catch (e) { showToast(e.message, 'error'); }
    finally { setGeneratingQ(false); }
  };

  const handleSaveQuestion = async (form) => {
    try {
      if (modalQuestion?.id) {
        await updateQuestion(modalQuestion.id, form);
      } else {
        await createQuestion({ ...form, concept_id: selectedConcept.id, course_id: courseId });
      }
      showToast('Question saved', 'success');
      setShowQModal(false);
      const qs = await getConceptQuestions(selectedConcept.id);
      setQuestions(qs);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleApprove = async (q) => {
    try {
      await updateQuestion(q.id, { is_approved: !q.is_approved });
      const qs = await getConceptQuestions(selectedConcept.id);
      setQuestions(qs);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleDelete = async (qId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await deleteQuestion(qId);
      setQuestions(qs => qs.filter(q => q.id !== qId));
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleRemoveLinkedVideo = async (videoId) => {
    if (!window.confirm('Remove this video from the concept?')) return;
    try {
      await removeConceptVideo(selectedConcept.id, videoId);
      showToast('Video removed', 'success');
      loadCourse();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const diffStars = (n) => '★'.repeat(n) + '☆'.repeat(3 - n);

  if (!course) {
    return <div className="studio-page"><div className="loading-center"><div className="spinner" /></div></div>;
  }

  return (
    <div className="studio-page">
      <UnifiedHeader subRow={
        <>
          <div className="studio-subheader-left">
            <button className="studio-back-btn" onClick={() => navigate('/studio/teacher')}>
              <ArrowLeft size={15} /> {t.back || 'Back'}
            </button>
            <h1 className="studio-page-title">{course.title}</h1>
            <span className={`badge ${course.status === 'ready' ? 'badge-green' : 'badge-orange'}`}>{course.status}</span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Page range filter — only show when syllabus exists */}
            {course.syllabus_filename && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem' }}>
                <span style={{ color: '#888', whiteSpace: 'nowrap' }}>{tT.pageRange || 'Pages'}:</span>
                <input
                  type="number" min="1" value={pageFrom}
                  onChange={e => setPageFrom(e.target.value)}
                  placeholder={tT.pageFrom || 'From'}
                  style={{ width: 54, padding: '0.3rem 0.4rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.8rem', textAlign: 'center' }}
                />
                <span style={{ color: '#aaa' }}>–</span>
                <input
                  type="number" min="1" value={pageTo}
                  onChange={e => setPageTo(e.target.value)}
                  placeholder={tT.pageTo || 'To'}
                  style={{ width: 54, padding: '0.3rem 0.4rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.8rem', textAlign: 'center' }}
                />
              </div>
            )}
            {course.status === 'ready' && course.concepts?.length === 0 && (
              <button className="btn-primary" onClick={handleExtractConcepts} disabled={extracting}>
                {extracting ? <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={15} />}
                {t.teacher?.extractConcepts || 'Extract Concepts'}
              </button>
            )}
            {course.concepts?.length > 0 && (
              <button className="btn-secondary" onClick={handleExtractConcepts} disabled={extracting}>
                <RefreshCw size={15} /> {t.teacher?.reExtract || 'Re-extract'}
              </button>
            )}
            {course.syllabus_r2_url && (
              <button
                onClick={() => setShowPdfViewer(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.85rem', borderRadius: 8, border: '1px solid #e0d6f5', background: showPdfViewer ? '#5B35A0' : 'white', color: showPdfViewer ? 'white' : '#5B35A0', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
              >
                <BookOpen size={14} /> {showPdfViewer ? (tT.hidePdf || 'Hide PDF') : (tT.viewPdf || 'View PDF')}
              </button>
            )}
            <button
              onClick={() => handleOpenChat()}
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.85rem', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#5B35A0,#667eea)', color: 'white', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
            >
              <MessageSquare size={14} /> {tT.teachingAssistant || 'Teaching Assistant'}
            </button>
            {course.syllabus_filename && (
              <button
                onClick={() => setSyllabusDeleteConfirm(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.85rem', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff5f5', color: '#dc2626', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500 }}
                title={`Remove syllabus: ${course.syllabus_filename}`}>
                <Trash2 size={14} /> Syllabus
              </button>
            )}
          </div>
        </>
      } />

      <div className="studio-main" style={{ padding: '1.5rem' }}>

        {/* Scanned PDF warning */}
        {course.parsed_structure?.is_scanned && (
          <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#92400e', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.1rem' }}>⚠️</span>
            <span>{tT.scannedPdfWarning || 'Scanned PDF detected. Text extraction may be incomplete or inaccurate. For best results, upload a text-based (digitally created) PDF.'}</span>
          </div>
        )}

        {/* Detected language notice */}
        {course.parsed_structure?.detected_language && course.parsed_structure.detected_language !== 'en' && (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.83rem', color: '#166534', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span>🌐</span>
            <span>{tT.detectedLanguage || 'PDF language detected'}: <strong>{ { fi: 'Finnish 🇫🇮', sv: 'Swedish 🇸🇪', de: 'German 🇩🇪', fr: 'French 🇫🇷', es: 'Spanish 🇪🇸', et: 'Estonian', no: 'Norwegian 🇳🇴' }[course.parsed_structure.detected_language] || course.parsed_structure.detected_language.toUpperCase() }</strong>. {tT.detectedLanguageSuffix || 'Concepts will be extracted in this language.'}</span>
          </div>
        )}

        {/* PDF Viewer modal */}
        {showPdfViewer && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
            onClick={() => setShowPdfViewer(false)}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: 'white', borderRadius: 14, width: '100%', maxWidth: 940, height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
            >
              <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e8edf5', display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                <BookOpen size={16} color="#5B35A0" />
                <span style={{ fontWeight: 700, fontSize: '0.9rem', flex: 1, color: '#1a1a2e' }}>{course?.title}</span>
                <button onClick={() => setShowPdfViewer(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: 4, display: 'flex', borderRadius: 6 }}>
                  <X size={20} />
                </button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
                <TeacherPdfViewer
                  courseId={courseId}
                  course={course}
                  tT={tT}
                  onOpenChat={(text) => { handleOpenChat(text); setShowPdfViewer(false); }}
                />
              </div>
            </div>
          </div>
        )}

        <div className="ce-layout">

          {/* Concept list */}
          <div className={`ce-concepts-panel${mobilePanel === 'detail' ? ' mobile-hidden' : ''}`}>
            <div className="studio-card" style={{ padding: '1rem' }}>
              <button
                onClick={() => setConceptsExpanded(e => !e)}
                style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 0, marginBottom: conceptsExpanded ? '0.75rem' : 0 }}
              >
                <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 700, color: '#5B35A0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t.teacher?.concepts || 'Concepts'} <span style={{ color: '#aaa', fontWeight: 400 }}>({course.concepts?.length || 0})</span>
                </h3>
                <ChevronDown size={14} style={{ color: '#aaa', transform: conceptsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
              </button>
              {conceptsExpanded && (course.concepts?.length === 0 ? (
                <p style={{ color: '#aaa', fontSize: '0.85rem' }}>
                  {course.status === 'ready'
                    ? (t.teacher?.clickExtract || 'Click "Extract Concepts" to begin')
                    : (t.teacher?.processingWait || 'Waiting for PDF processing...')}
                </p>
              ) : (
                <>
                  {/* Select-mode toolbar */}
                  <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                    <button
                      onClick={() => { setSelectMode(s => !s); setSelectedIds(new Set()); }}
                      style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', borderRadius: 6, border: '1px solid #ccc', background: selectMode ? '#5B35A0' : '#f5f5f5', color: selectMode ? '#fff' : '#555', cursor: 'pointer' }}>
                      {selectMode ? 'Cancel' : 'Select'}
                    </button>
                    {selectMode && selectedIds.size > 0 && (
                      <button
                        onClick={() => setDeleteConfirm({ bulk: true })}
                        style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Trash2 size={11} /> Delete {selectedIds.size}
                      </button>
                    )}
                    {selectMode && (
                      <button
                        onClick={() => setSelectedIds(new Set(course.concepts.map(c => c.id)))}
                        style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', borderRadius: 6, border: '1px solid #ccc', background: '#f5f5f5', color: '#555', cursor: 'pointer' }}>
                        All
                      </button>
                    )}
                  </div>

                  <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={course.concepts.map(c => c.id)} strategy={verticalListSortingStrategy}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                        {course.concepts.map((c, idx) => (
                          <SortableConceptItem
                            key={c.id}
                            concept={c}
                            index={idx}
                            total={course.concepts.length}
                            isSelected={selectedConcept?.id === c.id}
                            selectMode={selectMode}
                            renamingId={renamingId}
                            renameValue={renameValue}
                            renameSaving={renameSaving}
                            selectedIds={selectedIds}
                            onSelect={(concept) => {
                              setSelectedConcept(concept); selectedConceptIdRef.current = concept.id;
                              setMobilePanel('detail'); setPlayingVideoId(null); setSourceData(null);
                              if (detailPanelRef.current) detailPanelRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            onStartRename={(concept) => { setRenamingId(concept.id); setRenameValue(concept.title); }}
                            onRenameChange={setRenameValue}
                            onSaveRename={handleSaveRename}
                            onCancelRename={() => setRenamingId(null)}
                            onDelete={(concept) => setDeleteConfirm({ concept })}
                            onToggleSelect={toggleSelectConcept}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </>
              ))}
            </div>

            {/* Assignments section */}
            <div className="studio-card" style={{ padding: '1rem', marginTop: '0.75rem' }}>
              <button
                onClick={() => setAssignmentsExpanded(e => !e)}
                style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 0 }}
              >
                <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 700, color: '#5B35A0', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <ClipboardList size={14} />
                  {tT.assignments || 'Assignments'}
                  <span style={{ color: '#aaa', fontWeight: 400 }}>({courseAssignments.length})</span>
                </h3>
                <ChevronDown size={14} style={{ color: '#aaa', transform: assignmentsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>

              {assignmentsExpanded && (
                <div style={{ marginTop: '0.75rem' }}>
                  {courseAssignments.length === 0 ? (
                    <p style={{ color: '#aaa', fontSize: '0.82rem', margin: '0 0 0.75rem' }}>
                      {tT.noAssignments || 'No assignments yet'}
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.75rem' }}>
                      {courseAssignments.map(a => (
                        <div key={a.id} style={{
                          display: 'flex', alignItems: 'center', gap: '0.4rem',
                          padding: '0.5rem 0.65rem', borderRadius: 9,
                          background: '#fafbff', border: '1px solid #e8ecf0',
                        }}>
                          <span style={{ flex: 1, fontSize: '0.82rem', fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.title}
                          </span>
                          <span style={{
                            fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, flexShrink: 0,
                            background: a.is_published ? '#dcfce7' : '#fef3c7',
                            color: a.is_published ? '#166534' : '#92400e',
                          }}>
                            {a.is_published ? (tT.published || 'Live') : (tT.draft || 'Draft')}
                          </span>
                          <button
                            title={a.is_published ? (tT.unpublish || 'Unpublish') : (tT.publish || 'Publish')}
                            onClick={async () => {
                              try { await togglePublishAssignment(a.id); loadAssignments(); }
                              catch (e) { showToast(e.message, 'error'); }
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: a.is_published ? '#f59e0b' : '#4caf50', padding: '0 0.2rem', display: 'flex', alignItems: 'center' }}
                          >
                            <Send size={12} />
                          </button>
                          <button
                            title={tT.edit || 'Edit'}
                            onClick={() => { setEditingAssignment(a); setShowAssignmentForm(true); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', padding: '0 0.2rem', display: 'flex', alignItems: 'center' }}
                            onMouseEnter={e => e.currentTarget.style.color = '#5B35A0'}
                            onMouseLeave={e => e.currentTarget.style.color = '#ccc'}
                          >
                            <Edit2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    className="btn-primary"
                    style={{ width: '100%', padding: '0.45rem', fontSize: '0.82rem', justifyContent: 'center' }}
                    onClick={() => { setEditingAssignment(null); setShowAssignmentForm(true); }}
                  >
                    <Plus size={14} /> {tT.createAssignment || 'Create Assignment'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Concept detail */}
          {selectedConcept ? (
            <div ref={detailPanelRef} className={`ce-detail-panel${mobilePanel === 'concepts' ? ' mobile-hidden' : ''}`}>
              <button className="ce-mobile-back" onClick={() => setMobilePanel('concepts')}>
                <ArrowLeft size={13} /> {t.teacher?.concepts || 'Concepts'}
              </button>
              <div className="studio-card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <h2 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e' }}>
                      {selectedConcept.title}
                    </h2>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.88rem', lineHeight: 1.5 }}>
                      {selectedConcept.summary}
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem', flexShrink: 0 }}>
                    <div style={{ fontSize: '1.2rem', color: '#f59e0b' }}>
                      {diffStars(selectedConcept.difficulty || 1)}
                    </div>
                    <button
                      onClick={() => sourceData ? setSourceData(null) : handleShowSource(selectedConcept)}
                      disabled={sourceLoading}
                      style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', borderRadius: 6, border: '1px solid #c4b5f0', background: sourceData ? '#f3eeff' : '#faf8ff', color: '#5B35A0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      {sourceLoading ? <Loader size={11} className="spin" /> : <FileText size={11} />}
                      {sourceData ? 'Hide source' : (selectedConcept.page_reference ? `Source p.${selectedConcept.page_reference}` : 'Source')}
                    </button>
                  </div>
                </div>

                {/* Source pages panel */}
                {sourceData && (
                  <div style={{ background: '#f8f6ff', border: '1px solid #d9d0f5', borderRadius: 10, padding: '0.85rem', marginBottom: '1rem', fontSize: '0.82rem' }}>
                    <div style={{ fontWeight: 700, color: '#5B35A0', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <FileText size={13} /> Syllabus source — pages {sourceData.pages.join(', ')}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      {sourceData.excerpts.map((ex, i) => (
                        <div key={i} style={{ background: '#fff', borderRadius: 8, padding: '0.6rem 0.75rem', border: '1px solid #e8e0ff' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                            <span style={{ background: '#5B35A0', color: '#fff', borderRadius: 4, padding: '0.1rem 0.45rem', fontSize: '0.7rem', fontWeight: 700 }}>p.{ex.page}</span>
                            {ex.section && <span style={{ color: '#888', fontSize: '0.72rem' }}>{ex.section}</span>}
                          </div>
                          <p style={{ margin: 0, color: '#444', lineHeight: 1.5, fontSize: '0.8rem' }}>
                            {ex.text}{ex.text.length >= 400 ? '…' : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video section */}
                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '1rem' }}>

                  {/* Primary video */}
                  {['ready', 'completed'].includes(selectedConcept.video_status) && selectedConcept.video_url ? (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#5B35A0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          ▶ {tT.primaryVideo || 'Primary Video'}
                        </span>
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <button className="btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
                            onClick={() => setShowTranscriptView(true)}>
                            <FileText size={12} /> {tT.transcript || 'Transcript'}
                          </button>
                          <button className="btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
                            onClick={() => setShowTranscriptModal(true)} disabled={generatingV}>
                            <RefreshCw size={12} /> {tT.regenerate || 'Regenerate'}
                          </button>
                        </div>
                      </div>
                      {playingVideoId === 'primary' ? (
                        <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#000', aspectRatio: '16/9' }}>
                          <video src={selectedConcept.video_url} controls autoPlay style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} onEnded={() => setPlayingVideoId(null)} />
                        </div>
                      ) : (
                        <div onClick={() => setPlayingVideoId('primary')} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', background: '#111', aspectRatio: '16/9' }}>
                          {selectedConcept.thumbnail_url ? (
                            <img src={selectedConcept.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e', minHeight: 120 }}>
                              <Video size={32} color="#667eea" />
                            </div>
                          )}
                          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}>
                              <Video size={22} color="#667eea" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : selectedConcept.video_status === 'queued' || selectedConcept.video_status === 'rendering' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#ff9800', fontSize: '0.88rem', marginBottom: '1rem', padding: '0.75rem', background: '#fff8f0', borderRadius: 10 }}>
                      <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} />
                      {tT.renderingVideo || 'Rendering primary video… this takes 1–2 minutes'}
                    </div>
                  ) : (
                    /* No video yet — Step-by-step guide */
                    <div style={{ background: '#f8f9ff', borderRadius: 12, padding: '1rem', marginBottom: '1rem', border: '1.5px dashed #c5cff5' }}>
                      <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#555', fontWeight: 600 }}>
                        {tT.noVideoYet || "No video yet. Here's how it works:"}
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.85rem' }}>
                        {[
                          ['1', tT.videoStep1 || 'AI writes a transcript/synopsis for this concept'],
                          ['2', tT.videoStep2 || 'You read, edit, or adjust the transcript'],
                          ['3', tT.videoStep3 || 'Click Generate — video renders in ~1 min'],
                        ].map(([n, text]) => (
                          <div key={n} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                            <span style={{ background: '#5B35A0', color: '#fff', borderRadius: '50%', width: 18, height: 18, fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{n}</span>
                            <span style={{ fontSize: '0.83rem', color: '#555' }}>{text}</span>
                          </div>
                        ))}
                      </div>
                      <button className="btn-primary" style={{ width: '100%' }}
                        onClick={() => setShowTranscriptModal(true)} disabled={generatingV}>
                        {generatingV ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Video size={14} />}
                        {generatingV ? (tT.starting || 'Starting…') : (tT.previewTranscriptBtn || 'Preview Transcript & Generate Video')}
                      </button>
                    </div>
                  )}

                  {/* Additional / linked videos */}
                  {(selectedConcept.linked_videos || []).length > 0 && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {tT.additionalVideos || 'Additional Videos'}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {(selectedConcept.linked_videos || []).map(lv => {
                          const isRendering = ['queued','processing','rendering'].includes(lv.status);
                          const isError = lv.status === 'error';
                          const isReady = ['ready','completed'].includes(lv.status) && lv.video_url;
                          return (
                            <div key={lv.id} style={{ background: '#fafbff', borderRadius: 10, padding: '0.5rem', border: `1px solid ${isError ? '#fca5a5' : '#e8ecf0'}` }}>
                              {/* Video player / thumbnail / status */}
                              {isReady && playingVideoId === lv.id ? (
                                <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', background: '#000', aspectRatio: '16/9' }}>
                                  <video src={lv.video_url} controls autoPlay style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} onEnded={() => setPlayingVideoId(null)} />
                                </div>
                              ) : isReady ? (
                                <div onClick={() => setPlayingVideoId(lv.id)} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: '#111', aspectRatio: '16/9' }}>
                                  {lv.thumbnail_url
                                    ? <img src={lv.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e', minHeight: 100 }}><Video size={28} color="#667eea" /></div>
                                  }
                                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}>
                                      <Video size={18} color="#667eea" />
                                    </div>
                                  </div>
                                </div>
                              ) : isRendering ? (
                                <div style={{ aspectRatio: '16/9', background: '#1a1a2e', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', minHeight: 80 }}>
                                  <Loader size={22} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
                                  <span style={{ fontSize: '0.75rem', color: '#667eea' }}>{tT.renderingVideo || 'Rendering…'}</span>
                                </div>
                              ) : isError ? (
                                <div style={{ aspectRatio: '16/9', background: '#fff0f0', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', minHeight: 80 }}>
                                  <span style={{ fontSize: '0.78rem', color: '#ef4444', fontWeight: 600 }}>⚠ {tT.videoFailed || 'Render failed'}</span>
                                </div>
                              ) : (
                                <div style={{ aspectRatio: '16/9', background: '#1a1a2e', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80 }}>
                                  <Video size={28} color="#667eea" />
                                </div>
                              )}

                              {/* Title + actions row */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.35rem', gap: '0.3rem' }}>
                                <span style={{ fontSize: '0.78rem', color: '#555', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {lv.title || `Video #${lv.id}`}
                                </span>
                                <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                                  {isReady && (
                                    <button className="btn-icon" title={tT.transcript || 'Transcript'}
                                      onClick={() => { setTranscriptViewVideoId(lv.id); setShowTranscriptView(true); }}>
                                      <FileText size={13} color="#5B35A0" />
                                    </button>
                                  )}
                                  {(isError || isReady) && (
                                    <button
                                      className="btn-icon"
                                      title={tT.regenerate || 'Regenerate'}
                                      disabled={isRendering}
                                      onClick={async () => {
                                        try {
                                          await regenerateLinkedVideo(selectedConcept.id, lv.id);
                                          showToast(tT.starting || 'Regenerating…', 'success');
                                          loadCourse();
                                        } catch (e) { showToast(e.message, 'error'); }
                                      }}
                                    >
                                      <RefreshCw size={13} color="#5B35A0" />
                                    </button>
                                  )}
                                  <button className="btn-icon" title={tT.delete || 'Remove'} onClick={() => handleRemoveLinkedVideo(lv.id)}>
                                    <Trash2 size={13} color="#f44336" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Action buttons row */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                    <button className="btn-secondary" style={{ fontSize: '0.82rem' }}
                      onClick={() => setShowExtraVideoModal(true)}>
                      <Plus size={14} /> {tT.addAnotherVideo || 'Add Another Video'}
                    </button>
                    <button className="btn-secondary" style={{ fontSize: '0.82rem' }}
                      onClick={() => setShowVideoPicker(true)}>
                      <Plus size={14} /> {t.teacher?.addFromLibrary || 'Add from Library'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Questions */}
              <div className="studio-card">
                <div className="section-header">
                  <h2>{t.teacher?.questions || 'Questions'}</h2>
                  <div className="section-header-actions">
                    <button className="btn-secondary" onClick={() => setShowGenQModal(true)} disabled={generatingQ}>
                      {generatingQ ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={14} />}
                      {t.teacher?.generateQuestions || 'Generate with AI'}
                    </button>
                    <button className="btn-primary" onClick={() => { setModalQuestion(null); setShowQModal(true); }}>
                      <Plus size={14} />
                      {t.teacher?.addQuestion || 'Add'}
                    </button>
                  </div>
                </div>

                {loadingQuestions ? (
                  <div className="loading-center" style={{ padding: '2rem' }}><div className="spinner spinner-sm" /></div>
                ) : questions.length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.88rem', textAlign: 'center', padding: '1.5rem 0' }}>
                    {t.teacher?.noQuestionsYet || 'No questions yet. Generate with AI or add manually.'}
                  </p>
                ) : (
                  questions.map(q => (
                    <div key={q.id} className="question-item">
                      <div className="question-item-header">
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                            <span className={`badge ${q.type === 'multiple_choice' ? 'badge-blue' : q.type === 'numeric' ? 'badge-orange' : 'badge-purple'}`}>
                              {q.type === 'multiple_choice' ? (tT.multipleChoice || 'Multiple Choice')
                                : q.type === 'numeric' ? (tT.numeric || 'Numeric')
                                : (tT.openEnded || 'Open Ended')}
                            </span>
                            <span className={`badge ${q.is_approved ? 'badge-green' : 'badge-grey'}`}>
                              {q.is_approved ? `✓ ${tT.approved || 'Approved'}` : (tT.pending || 'Pending')}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#999' }}>{'★'.repeat(q.difficulty || 1)}</span>
                          </div>
                          <p className="question-item-text">{q.text}</p>
                        </div>
                        <div className="question-item-actions">
                          <button className="btn-icon" title={q.is_approved ? (tT.unapprove || 'Unapprove') : (tT.approve || 'Approve')}
                            onClick={() => handleApprove(q)}>
                            <Check size={16} color={q.is_approved ? '#4caf50' : '#ccc'} />
                          </button>
                          <button className="btn-icon" title="Edit"
                            onClick={() => { setModalQuestion(q); setShowQModal(true); }}>
                            <Edit2 size={15} />
                          </button>
                          <button className="btn-icon" title="Delete" onClick={() => handleDelete(q.id)}>
                            <Trash2 size={15} color="#f44336" />
                          </button>
                          <button className="btn-icon" onClick={() => setExpandedQ(expandedQ === q.id ? null : q.id)}>
                            {expandedQ === q.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                          </button>
                        </div>
                      </div>
                      {expandedQ === q.id && (
                        <div style={{ marginTop: '0.5rem', background: '#f8f9ff', borderRadius: 8, padding: '0.75rem', fontSize: '0.82rem', color: '#555' }}>
                          {q.type === 'multiple_choice' && q.options && (
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>{t.teacher?.options || 'Options'}:</strong>
                              <ul style={{ margin: '0.3rem 0 0 1rem', padding: 0 }}>
                                {(Array.isArray(q.options) ? q.options : JSON.parse(q.options)).map((opt, i) => (
                                  <li key={i} style={{ color: opt === q.correct_answer ? '#4caf50' : '#555', fontWeight: opt === q.correct_answer ? 700 : 400 }}>
                                    {opt === q.correct_answer ? '✓ ' : ''}{opt}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {q.type === 'numeric' && q.correct_answer && (
                            <div style={{ marginBottom: q.ai_explanation ? '0.5rem' : 0 }}>
                              <strong>{t.teacher?.correctAnswer || 'Correct Answer'}:</strong> {q.correct_answer}
                            </div>
                          )}
                          {q.model_answer && (
                            <div style={{ marginBottom: q.ai_explanation ? '0.5rem' : 0 }}>
                              <strong>{t.teacher?.modelAnswer || 'Model answer'}:</strong> {q.model_answer}
                            </div>
                          )}
                          {q.ai_explanation && (
                            <div style={{ color: '#777', fontStyle: 'italic' }}>
                              <strong>{t.teacher?.explanation || 'Explanation'}:</strong> {q.ai_explanation}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

            </div>
          ) : (
            <div className={`ce-detail-panel${mobilePanel === 'concepts' ? ' mobile-hidden' : ''}`}>
              <div className="studio-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                <p style={{ color: '#aaa' }}>{t.teacher?.selectConcept || 'Select a concept from the list'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Syllabus delete confirmation modal */}
      {syllabusDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '1.5rem', maxWidth: 380, width: '90vw', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
              <Trash2 size={18} color="#ef4444" />
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e' }}>Remove syllabus?</span>
            </div>
            <p style={{ color: '#555', fontSize: '0.88rem', margin: '0 0 1.25rem', lineHeight: 1.5 }}>
              This will remove <strong>{course.syllabus_filename}</strong> and all its indexed content. Existing concepts and videos are not affected.
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setSyllabusDeleteConfirm(false)}
                style={{ padding: '0.5rem 1.1rem', borderRadius: 8, border: '1px solid #ddd', background: '#f5f5f5', cursor: 'pointer', fontSize: '0.88rem' }}>
                Cancel
              </button>
              <button disabled={syllabusDeleting} onClick={handleDeleteSyllabus}
                style={{ padding: '0.5rem 1.1rem', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {syllabusDeleting && <Loader size={13} className="spin" />}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '1.5rem', maxWidth: 380, width: '90vw', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
              <Trash2 size={18} color="#ef4444" />
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e' }}>
                {deleteConfirm.bulk ? `Delete ${selectedIds.size} concept(s)?` : `Delete "${deleteConfirm.concept?.title}"?`}
              </span>
            </div>
            <p style={{ color: '#555', fontSize: '0.88rem', margin: '0 0 1.25rem', lineHeight: 1.5 }}>
              {deleteConfirm.bulk
                ? `This will permanently remove ${selectedIds.size} concept(s) and their practice questions. Linked videos will be unlinked but not deleted.`
                : `This will permanently remove this concept and its practice questions. Linked videos will be unlinked but not deleted.`}
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)}
                style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #ddd', background: '#f5f5f5', cursor: 'pointer', fontSize: '0.88rem' }}>
                Cancel
              </button>
              <button
                disabled={bulkDeletePending}
                onClick={() => deleteConfirm.bulk ? handleBulkDelete() : handleDeleteConcept(deleteConfirm.concept)}
                style={{ padding: '0.5rem 1.1rem', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {bulkDeletePending && <Loader size={13} className="spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showGenQModal && (
        <GenerateQModal
          onGenerate={handleGenerateQuestions}
          onClose={() => setShowGenQModal(false)}
        />
      )}

      {showQModal && (
        <QuestionModal
          question={modalQuestion}
          onSave={handleSaveQuestion}
          onClose={() => setShowQModal(false)}
        />
      )}

      {showVideoPicker && selectedConcept && (
        <VideoPickerModal
          conceptId={selectedConcept.id}
          onClose={() => setShowVideoPicker(false)}
          onAdded={() => { showToast('Video linked', 'success'); loadCourse(); }}
          t={t}
        />
      )}

      {showTranscriptModal && selectedConcept && (
        <TranscriptModal
          concept={selectedConcept}
          onClose={() => setShowTranscriptModal(false)}
          onGenerate={handleGenerateVideoFromTranscript}
        />
      )}

      {showTranscriptView && selectedConcept && (
        <TranscriptViewModal
          conceptId={transcriptViewVideoId ? undefined : selectedConcept.id}
          videoId={transcriptViewVideoId || undefined}
          conceptTitle={selectedConcept.title}
          onClose={() => { setShowTranscriptView(false); setTranscriptViewVideoId(null); }}
        />
      )}

      {showExtraVideoModal && selectedConcept && (
        <ExtraVideoModal
          concept={selectedConcept}
          onClose={() => setShowExtraVideoModal(false)}
          onGenerate={handleGenerateExtraVideo}
        />
      )}

      {toast && (
        <div className={`studio-toast ${toast.type}`}>{toast.msg}</div>
      )}

      {showAssignmentForm && course && (
        <AssignmentForm
          courses={[{ id: course.id, title: course.title }]}
          initial={editingAssignment ? { ...editingAssignment, course_id: course.id } : { course_id: course.id }}
          onClose={() => { setShowAssignmentForm(false); setEditingAssignment(null); }}
          onSave={async (form) => {
            try {
              if (editingAssignment?.id) {
                await updateAssignment(editingAssignment.id, form);
                showToast('Assignment updated', 'success');
              } else {
                await createAssignment(form);
                showToast('Assignment created', 'success');
              }
              setShowAssignmentForm(false);
              setEditingAssignment(null);
              setAssignmentsExpanded(true);
              loadAssignments();
            } catch (e) { showToast(e.message, 'error'); }
          }}
        />
      )}

      {showTeacherChat && (
        <TeacherChatPanel
          courseId={courseId}
          course={course}
          tT={tT}
          language={language}
          initialText={chatInitialText}
          onClose={() => { setShowTeacherChat(false); setChatInitialText(''); }}
          onOpenAssignment={handleOpenAssignment}
        />
      )}
    </div>
  );
}
